import {
  DEFAULT_PAGE_MARGIN,
  PORTRAIT_PAGE_HEIGHT,
  PORTRAIT_PAGE_WIDTH,
} from './pagination.constants';

/**
 * Read a CSS custom property from the provided root element.
 * Why: printV2 reads vars from the print iframe root, while editor/runtime
 * paths read from the main document root.
 * Purity: impure (reads live DOM/computed styles).
 */
const getComputedStyleValue = (
  cssVariableName,
  rootElement = document.documentElement
) => {
  return getComputedStyle(rootElement)
    .getPropertyValue(cssVariableName)
    ?.trim();
};

/**
 * Parse an integer CSS custom property with fallback from a chosen root.
 * Purity: impure (delegates to computed-style DOM reads).
 */
const getIntCssVariable = (
  cssVariableName,
  fallbackValue,
  rootElement = document.documentElement
) => {
  return (
    parseInt(getComputedStyleValue(cssVariableName, rootElement), 10) ||
    fallbackValue
  );
};

/**
 * Resolve page top/bottom margin from CSS variables.
 * Purity: impure (reads live DOM/computed styles).
 */
export const getPageMargin = (fallbackValue = DEFAULT_PAGE_MARGIN) => {
  return getIntCssVariable('--page-margin-top-bottom', fallbackValue);
};

/**
 * Resolve printable content width from CSS page vars.
 * Purity: impure (reads live DOM/computed styles).
 */
export const getContentWidth = (
  pageWidthFallback = PORTRAIT_PAGE_WIDTH,
  marginLeftRightFallback = DEFAULT_PAGE_MARGIN
) => {
  const pageWidth = getIntCssVariable('--page-width', pageWidthFallback);
  const marginLeftRight = getIntCssVariable(
    '--page-margin-left-right',
    marginLeftRightFallback
  );
  const totalMargin = 2 * marginLeftRight;
  return pageWidth - totalMargin;
};

/**
 * Resolve printable content height from CSS page vars.
 * Purity: impure (reads live DOM/computed styles).
 */
export const getContentHeight = (
  pageHeightFallback = PORTRAIT_PAGE_HEIGHT,
  marginTopBottomFallback = DEFAULT_PAGE_MARGIN
) => {
  const pageHeight = getIntCssVariable('--page-height', pageHeightFallback);
  const marginTopBottom = getPageMargin(marginTopBottomFallback);
  const totalMargin = 2 * marginTopBottom;
  return pageHeight - totalMargin;
};

/**
 * Resolve document line-height token used by pagination/header-footer layout.
 * Purity: impure (reads live DOM/computed styles).
 */
export const getLineHeight = (fallbackValue = '1.5') => {
  return getComputedStyleValue('--line-height') || fallbackValue;
};

/**
 * Resolve configured widow/orphan minimum lines from CSS vars.
 * Accepts an optional root element (e.g., print iframe documentElement).
 * Purity: impure (reads live DOM/computed styles).
 */
export const getWidowOrphanMinLines = (
  fallbackValue = 1,
  rootElement = document.documentElement
) => {
  return getIntCssVariable('--widow-orphan-lines', fallbackValue, rootElement);
};

/**
 * Force a synchronous style/layout flush for a given document.
 * Purity: impure (forces browser layout calculation).
 */
export const flushDocumentLayout = (doc = document) => {
  if (!doc?.documentElement) return;
  // eslint-disable-next-line no-unused-expressions
  doc.documentElement.offsetHeight;
};

/**
 * Force an inline style override via `!important` priority.
 * Pass null/undefined to remove the property.
 * Why this helper exists:
 * - keeps print/pagination code intent-focused (`forceStyleOverride(...)`)
 *   instead of repeating raw `style.setProperty(..., 'important')` calls.
 * - centralizes "remove vs override" semantics used by V2 drift normalization.
 * Purity: impure (mutates DOM styles).
 */
export const forceStyleOverride = (el, propertyName, value) => {
  if (!el?.style || !propertyName) return;
  if (value == null) {
    el.style.removeProperty(propertyName);
    return;
  }
  el.style.setProperty(propertyName, String(value), 'important');
};

/**
 * Force multiple inline style overrides via `!important` priority.
 * Companion to `forceStyleOverride` for small style batches.
 * Purity: impure (mutates DOM styles).
 */
export const forceStyleOverrides = (el, styleMap = {}) => {
  if (!el?.style || !styleMap) return;
  Object.entries(styleMap).forEach(([propertyName, value]) => {
    forceStyleOverride(el, propertyName, value);
  });
};

/**
 * Safe `getBoundingClientRect` read helper.
 * Used by print/pagination code to make geometry reads explicit and guard nulls.
 * Purity: impure (reads live DOM geometry).
 */
export const getElementRect = (el) => {
  return el?.getBoundingClientRect?.() || null;
};

/**
 * Compute absolute delta between two rect numeric fields.
 * Returns 0 when either side is not finite.
 * Commonly used with `fieldName='height'` for drift-threshold checks.
 * Purity: pure with respect to function inputs.
 */
export const getRectDelta = (srcRect, dstRect, fieldName = 'height') => {
  const srcValue = srcRect?.[fieldName];
  const dstValue = dstRect?.[fieldName];
  if (!Number.isFinite(srcValue) || !Number.isFinite(dstValue)) return 0;
  return Math.abs(srcValue - dstValue);
};

/**
 * Read a `data-*` attribute from an element.
 * Purity: pure with respect to function inputs.
 */
export const getElementDataAttribute = (el, attributeName = 'id') => {
  return (
    (el?.getAttribute &&
      el.getAttribute(`data-${String(attributeName || '').trim()}`)) ||
    ''
  );
};

/**
 * Read canonical `data-id` from an element.
 * Purity: pure with respect to function inputs.
 */
export const getElementDataId = (el) => {
  return getElementDataAttribute(el, 'id');
};

/**
 * Build canonical node identity key from raw parts.
 * Prefers `data-id`; falls back to `tag`.
 * Always appends `::index` to keep keys unique among repeated siblings
 * sharing the same `data-id`.
 * Why this matters: print planning can split one logical source node into
 * multiple fragments/clones that still carry the original `data-id`; without
 * the index suffix those fragments can collide in contract/source maps.
 * Purity: pure with respect to function inputs.
 */
export const buildStableNodeKey = (dataId, tagName, index) => {
  const normalizedDataId = String(dataId || '').trim();
  return `${normalizedDataId || String(tagName || 'node').toLowerCase()}::${index}`;
};

/**
 * Build the canonical top-level node identity key used by pagination flows.
 * Prefers `data-id`; falls back to `tag::index` for nodes without identifiers.
 * Purity: pure with respect to function inputs.
 */
export const getElementStableKey = (el, index) => {
  return buildStableNodeKey(getElementDataId(el), el?.tagName, index);
};

/**
 * Normalize failed image rendering to a deterministic placeholder footprint.
 *
 * Why:
 * - Browsers render broken `<img>` elements with different defaults, which can
 *   shift surrounding layout and produce non-deterministic measurements.
 *
 * Behavior:
 * - applies a fixed placeholder size
 * - recenters the nearest editor wrapper used by resize-image node views
 * - centers the image element itself
 *
 * @param {HTMLImageElement | null} img
 * @param {string} brokenSizePx
 * @returns {boolean} true when image was normalized
 *
 * Purity: impure (mutates DOM styles).
 */
export const normalizeBrokenImageElement = (img, brokenSizePx = '18px') => {
  if (!img || !(img.complete && img.naturalWidth === 0)) return false;

  forceStyleOverrides(img, {
    width: brokenSizePx,
    height: brokenSizePx,
    'object-fit': 'contain',
  });

  const root = img.closest('.tiptap, .tiptapV3');
  let wrapper = img.parentElement;
  if (root) {
    while (wrapper && wrapper.parentElement !== root) {
      wrapper = wrapper.parentElement;
    }
  } else {
    wrapper = null;
  }
  if (!wrapper) {
    wrapper = img.closest('.tiptapV3 > div, .tiptap > div');
  }
  if (wrapper) {
    forceStyleOverrides(wrapper, {
      display: 'block',
      position: 'relative',
      left: '50%',
      transform: 'translateX(-50%)',
      width: 'fit-content',
      'max-width': '100%',
      'text-align': 'center',
    });
  }
  forceStyleOverrides(img, {
    display: 'block',
    'margin-left': 'auto',
    'margin-right': 'auto',
  });
  return true;
};

/**
 * Returns true when an <img> has a non-empty src value.
 * Useful for filtering out editor separator/broken placeholder image nodes.
 * Purity: pure with respect to function inputs.
 */
export const imageHasRenderableSrc = (img) => {
  const src = img?.getAttribute?.('src');
  return typeof src === 'string' && src.trim().length > 0;
};

/**
 * Compute childNode-index path from root to target.
 * Purity: pure with respect to function inputs.
 */
export const getNodePath = (root, target) => {
  const path = [];
  let n = target;
  while (n && n !== root) {
    const p = n.parentNode;
    if (!p) return null;
    path.unshift(Array.from(p.childNodes).indexOf(n));
    n = p;
  }
  return n === root ? path : null;
};

/**
 * Resolve a node by childNode-index path.
 * Purity: pure with respect to function inputs.
 */
export const resolveNodePath = (root, path) => {
  let n = root;
  for (const i of path) {
    n = n.childNodes[i];
    if (!n) return null;
  }
  return n;
};

/**
 * Remove siblings after `node` up to `root`.
 * Purity: impure (mutates DOM tree).
 */
export const pruneAfter = (root, node) => {
  let n = node;
  while (n && n !== root) {
    while (n.nextSibling) n.parentNode.removeChild(n.nextSibling);
    n = n.parentNode;
  }
};

/**
 * Remove siblings before `node` up to `root`.
 * Purity: impure (mutates DOM tree).
 */
export const pruneBefore = (root, node) => {
  let n = node;
  while (n && n !== root) {
    while (n.previousSibling) n.parentNode.removeChild(n.previousSibling);
    n = n.parentNode;
  }
};
